import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Activity, 
  User, 
  Settings, 
  FileText, 
  GitCommit,
  Database,
  Shield,
  Clock,
  Play,
  Pause,
  RotateCcw,
  Filter,
  Eye
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';

interface ActivityEvent {
  id: string;
  type: 'user' | 'system' | 'api' | 'workflow' | 'security';
  action: string;
  description: string;
  actor: {
    type: 'user' | 'system' | 'service';
    id: string;
    name: string;
    avatar?: string;
  };
  target?: {
    type: 'ticket' | 'project' | 'file' | 'user' | 'system';
    id: string;
    name: string;
  };
  timestamp: Date;
  metadata?: Record<string, any>;
  severity: 'low' | 'medium' | 'high' | 'critical';
  source: string;
  tags?: string[];
}

interface RealTimeActivityFeedProps {
  maxItems?: number;
  autoRefresh?: boolean;
  refreshInterval?: number;
  showFilters?: boolean;
  enablePause?: boolean;
  height?: string;
}

export const RealTimeActivityFeed: React.FC<RealTimeActivityFeedProps> = ({
  maxItems = 50,
  autoRefresh = true,
  refreshInterval = 5000,
  showFilters = true,
  enablePause = true,
  height = '400px'
}) => {
  const [activities, setActivities] = useState<ActivityEvent[]>([]);
  const [filteredActivities, setFilteredActivities] = useState<ActivityEvent[]>([]);
  const [isPaused, setIsPaused] = useState(false);
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);

  // Mock data generator for realistic activity simulation
  const generateMockActivity = (): ActivityEvent => {
    const activityTypes = [
      {
        type: 'user' as const,
        actions: [
          'logged in',
          'created ticket',
          'updated profile',
          'uploaded file',
          'joined meeting',
          'completed task',
          'reviewed document'
        ],
        actors: [
          { type: 'user' as const, id: 'user-1', name: 'Sarah Chen' },
          { type: 'user' as const, id: 'user-2', name: 'Mike Rodriguez' },
          { type: 'user' as const, id: 'user-3', name: 'Emily Johnson' },
          { type: 'user' as const, id: 'user-4', name: 'David Kim' }
        ]
      },
      {
        type: 'system' as const,
        actions: [
          'backup completed',
          'scheduled maintenance',
          'auto-scaling triggered',
          'health check passed',
          'database optimized',
          'cache cleared'
        ],
        actors: [
          { type: 'system' as const, id: 'sys-1', name: 'Backup Service' },
          { type: 'system' as const, id: 'sys-2', name: 'Monitoring Service' },
          { type: 'system' as const, id: 'sys-3', name: 'ETL Engine' },
          { type: 'system' as const, id: 'sys-4', name: 'Security Scanner' }
        ]
      },
      {
        type: 'workflow' as const,
        actions: [
          'workflow started',
          'approval pending',
          'step completed',
          'workflow failed',
          'escalation triggered',
          'automation executed'
        ],
        actors: [
          { type: 'service' as const, id: 'wf-1', name: 'Approval Workflow' },
          { type: 'service' as const, id: 'wf-2', name: 'SOMA Processor' },
          { type: 'service' as const, id: 'wf-3', name: 'FixBee Service' },
          { type: 'service' as const, id: 'wf-4', name: 'HITL Router' }
        ]
      }
    ];

    const randomType = activityTypes[Math.floor(Math.random() * activityTypes.length)];
    const randomAction = randomType.actions[Math.floor(Math.random() * randomType.actions.length)];
    const randomActor = randomType.actors[Math.floor(Math.random() * randomType.actors.length)];

    const targets = [
      { type: 'ticket' as const, id: 'CB-2025-001', name: 'Login Issues' },
      { type: 'project' as const, id: 'proj-001', name: 'HiveCore v2.0' },
      { type: 'file' as const, id: 'file-001', name: 'requirements.pdf' },
      { type: 'user' as const, id: 'user-005', name: 'Admin User' }
    ];

    const severities: Array<'low' | 'medium' | 'high' | 'critical'> = ['low', 'medium', 'high'];
    
    return {
      id: `activity-${Date.now()}-${Math.random()}`,
      type: randomType.type,
      action: randomAction,
      description: `${randomActor.name} ${randomAction}${Math.random() > 0.7 ? ` for ${targets[Math.floor(Math.random() * targets.length)].name}` : ''}`,
      actor: randomActor,
      target: Math.random() > 0.5 ? targets[Math.floor(Math.random() * targets.length)] : undefined,
      timestamp: new Date(),
      severity: severities[Math.floor(Math.random() * severities.length)],
      source: `${randomActor.name} Service`,
      metadata: {
        ip: `192.168.1.${Math.floor(Math.random() * 255)}`,
        userAgent: 'Mozilla/5.0...',
        sessionId: `sess-${Math.random().toString(36).substr(2, 9)}`
      },
      tags: Math.random() > 0.5 ? ['automated', 'production'] : ['manual', 'staging']
    };
  };

  // Initialize with some mock data
  useEffect(() => {
    const initialActivities = Array.from({ length: 10 }, () => ({
      ...generateMockActivity(),
      timestamp: new Date(Date.now() - Math.random() * 60 * 60 * 1000) // Last hour
    })).sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

    setActivities(initialActivities);
  }, []);

  // Auto-refresh logic
  useEffect(() => {
    if (autoRefresh && !isPaused) {
      intervalRef.current = setInterval(() => {
        const newActivity = generateMockActivity();
        setActivities(prev => [newActivity, ...prev.slice(0, maxItems - 1)]);
      }, refreshInterval);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoRefresh, isPaused, refreshInterval, maxItems]);

  // Filter logic
  useEffect(() => {
    let filtered = activities;

    if (activeFilter !== 'all') {
      if (activeFilter === 'users') {
        filtered = activities.filter(a => a.type === 'user');
      } else if (activeFilter === 'system') {
        filtered = activities.filter(a => a.type === 'system');
      } else if (activeFilter === 'security') {
        filtered = activities.filter(a => a.type === 'security' || a.severity === 'critical');
      } else if (activeFilter === 'workflows') {
        filtered = activities.filter(a => a.type === 'workflow');
      }
    }

    setFilteredActivities(filtered);
  }, [activities, activeFilter]);

  // Auto-scroll logic
  useEffect(() => {
    if (autoScroll && containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [filteredActivities, autoScroll]);

  const getActivityIcon = (type: string, action: string) => {
    if (action.includes('login') || action.includes('security')) return Shield;
    if (action.includes('file') || action.includes('upload')) return FileText;
    if (action.includes('workflow') || action.includes('approval')) return GitCommit;
    if (action.includes('database') || action.includes('backup')) return Database;
    if (action.includes('user') || action.includes('profile')) return User;
    if (type === 'system') return Settings;
    return Activity;
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-red-400 bg-red-500/20';
      case 'high': return 'text-orange-400 bg-orange-500/20';
      case 'medium': return 'text-yellow-400 bg-yellow-500/20';
      default: return 'text-slate-400 bg-slate-500/20';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'user': return 'text-cyan-400';
      case 'system': return 'text-emerald-400';
      case 'security': return 'text-red-400';
      case 'workflow': return 'text-[#5A7BC4]';
      default: return 'text-slate-400';
    }
  };

  const formatTimestamp = (timestamp: Date) => {
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (seconds < 30) return 'just now';
    if (seconds < 60) return `${seconds}s ago`;
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return timestamp.toLocaleDateString();
  };

  const filterOptions = [
    { id: 'all', label: 'All Activities', count: activities.length },
    { id: 'users', label: 'User Actions', count: activities.filter(a => a.type === 'user').length },
    { id: 'system', label: 'System Events', count: activities.filter(a => a.type === 'system').length },
    { id: 'workflows', label: 'Workflows', count: activities.filter(a => a.type === 'workflow').length },
    { id: 'security', label: 'Security', count: activities.filter(a => a.type === 'security' || a.severity === 'critical').length }
  ];

  return (
    <Card className="control-bridge-glass-dark rounded-2xl">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <CardTitle className="text-white text-lg flex items-center gap-2">
          <Activity className="w-5 h-5" />
          Real-Time Activity
          <Badge variant="secondary" className="ml-2 bg-emerald-500/20 text-emerald-300">
            Live
          </Badge>
        </CardTitle>
        
        <div className="flex items-center gap-2">
          {showFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowFilterPanel(!showFilterPanel)}
              className="text-slate-400 hover:text-white"
            >
              <Filter className="w-4 h-4" />
            </Button>
          )}
          
          {enablePause && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsPaused(!isPaused)}
              className={`${isPaused ? 'text-emerald-400' : 'text-amber-400'} hover:text-white`}
            >
              {isPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
            </Button>
          )}
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setAutoScroll(!autoScroll)}
            className={`${autoScroll ? 'text-cyan-400' : 'text-slate-400'} hover:text-white`}
          >
            <Eye className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>

      {showFilters && showFilterPanel && (
        <div className="px-6 pb-4">
          <div className="flex flex-wrap gap-2">
            {filterOptions.map((option) => (
              <Button
                key={option.id}
                variant="ghost"
                size="sm"
                onClick={() => setActiveFilter(option.id)}
                className={`text-xs px-3 py-1 ${
                  activeFilter === option.id
                    ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
                }`}
              >
                {option.label}
                <Badge variant="secondary" className="ml-1 h-4 text-xs">
                  {option.count}
                </Badge>
              </Button>
            ))}
          </div>
        </div>
      )}
      
      <CardContent 
        ref={containerRef}
        className="space-y-2 overflow-y-auto"
        style={{ height }}
      >
        <AnimatePresence initial={false}>
          {filteredActivities.map((activity, index) => {
            const Icon = getActivityIcon(activity.type, activity.action);
            
            return (
              <motion.div
                key={activity.id}
                initial={{ opacity: 0, y: -20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.95 }}
                transition={{ 
                  duration: 0.3,
                  delay: index < 3 ? index * 0.05 : 0 // Only delay first 3 items
                }}
                className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/50 hover:bg-slate-800/50 transition-all duration-200"
              >
                <div className="flex items-start gap-3">
                  <div className={`p-1.5 rounded-lg ${getSeverityColor(activity.severity)}`}>
                    <Icon className={`w-3 h-3 ${getTypeColor(activity.type)}`} />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <p className="text-sm text-white font-medium line-clamp-1">
                          {activity.description}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-slate-400">
                            by {activity.actor.name}
                          </span>
                          {activity.target && (
                            <>
                              <span className="text-xs text-slate-400">→</span>
                              <span className="text-xs text-cyan-400">
                                {activity.target.name}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <span className="text-xs text-slate-500">
                          {formatTimestamp(activity.timestamp)}
                        </span>
                        {activity.severity !== 'low' && (
                          <Badge 
                            className={`ml-2 text-xs px-1.5 py-0.5 ${getSeverityColor(activity.severity)}`}
                          >
                            {activity.severity}
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    {activity.tags && activity.tags.length > 0 && (
                      <div className="flex gap-1 mt-2">
                        {activity.tags.map((tag) => (
                          <span
                            key={tag}
                            className="text-xs px-1.5 py-0.5 bg-slate-700/50 text-slate-400 rounded"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
        
        {filteredActivities.length === 0 && (
          <div className="text-center py-8 text-slate-400">
            <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No activities found</p>
          </div>
        )}
        
        {isPaused && (
          <div className="text-center py-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-500/20 text-amber-400 rounded-lg text-xs">
              <Pause className="w-3 h-3" />
              Activity feed paused
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
